import { LiveAnnouncer } from '@angular/cdk/a11y';
import { CdkAccordionModule } from '@angular/cdk/accordion';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  viewChild,
  viewChildren,
} from '@angular/core';
import { Town } from '@portal/common';
import { IconComponent } from '@shared/ui-shared';
import type { CitiesDialogData } from '../../services/dialog.service';
import { STATE_NAMES } from './state-names.constant';

@Component({
  selector: 'csl-cities-dialog',
  standalone: true,
  imports: [CommonModule, IconComponent, CdkAccordionModule],
  templateUrl: './cities-dialog.component.html',
  styleUrl: './cities-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CitiesDialogComponent implements AfterViewInit {
  private dialogRef = inject(DialogRef);
  private readonly dialogData = inject<CitiesDialogData>(DIALOG_DATA);
  private cities = this.dialogData.towns;
  private type = this.dialogData.type;

  private scrollContainer = viewChild.required<ElementRef<HTMLDivElement>>('scrollContainer');
  private sectionTabs = viewChildren<ElementRef<HTMLElement>, ElementRef>('sectionTab', { read: ElementRef });
  private closeButton = viewChild<ElementRef>('closeButton');
  private dialogTitle = viewChild<ElementRef>('dialogTitle');
  private liveAnnouncer = inject(LiveAnnouncer);

  stateSections = Object.entries(this.groupCitiesByState(this.cities))
    .sort(([stateA], [stateB]) => stateA.localeCompare(stateB))
    .map(([state, cities]) => ({
      state: (<Record<string, string>>STATE_NAMES)[state] || state,
      citiesCount: cities.length,
      citiesGroups: Object.entries(this.groupCities(cities.sort()))
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([, cities]) => cities),
    }));

  ngAfterViewInit() {
    // Focus on the dialog title heading for screen reader accessibility
    // This ensures the dialog and its purpose are immediately announced
    const titleRef = this.dialogTitle();
    if (titleRef) {
      const titleElement = titleRef instanceof ElementRef ? titleRef.nativeElement : titleRef;
      if (titleElement) {
        titleElement.tabIndex = -1;
        titleElement.focus();
        this.announceDialogOpened();
      }
    }
  }

  private announceDialogOpened(): void {
    const serviceType = this.isResidentialService() ? 'order' : 'request';
    const announcement = `Dialog opened. Online service ${serviceType} areas. This list is organized by state sections. Expand each state section to view available cities. Press Escape or use the close button at the top to close this dialog.`;
    this.liveAnnouncer.announce(announcement);
  }

  // Added scroll behavior to scroll to the last section tab when the dialog opens
  // constructor() {
  //   afterNextRender({
  //     read: () => {
  //       const scrollContainer = this.scrollContainer().nativeElement;
  //       const lastHeader = this.sectionTabs()[this.sectionTabs().length - 1]?.nativeElement;
  //       if (scrollContainer && lastHeader) {
  //         const scrollY = lastHeader.offsetTop ?? 0;
  //         scrollContainer.scrollTo({ top: scrollY, behavior: 'smooth' });
  //       }
  //     },
  //   });
  // }

  close() {
    this.dialogRef.close();
  }

  isResidentialService(): boolean {
    return this.type === 'residential';
  }

  private groupCitiesByState(cities: Town[]) {
    const grouped = cities.reduce(
      (acc, city) => {
        const stateCode = city.state.toUpperCase();
        if (!acc[stateCode]) {
          acc[stateCode] = [];
        }
        acc[stateCode].push(city.name);
        return acc;
      },
      {} as Record<string, string[]>,
    );

    return grouped;
  }

  private groupCities(cities: string[]) {
    const grouped = cities.reduce(
      (acc, city) => {
        const firstLetter = city.charAt(0).toUpperCase();
        if (!acc[firstLetter]) {
          acc[firstLetter] = [];
        }
        acc[firstLetter].push(city);
        return acc;
      },
      {} as Record<string, string[]>,
    );

    return grouped;
  }
}
